import React, { useState, useEffect } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { Toaster } from "sonner";
import { endpointCategories } from "./constants/endpoints";
import { getQueryParams } from "./utils/paramHelpers";
import { executeApiCall } from "./utils/apiUtils";
import { StyledContainer } from "./styles/StyledComponents";
import EndpointList from "./components/EndpointList";
import ResponsePanel from "./components/ResponsePanel";
import ConfirmationModal from "./components/ConfirmationModal";
import Header from "./components/Header";
import * as localService from "../../../services/localService";
import { Constants } from "../../../utils/constants";
import apiService from "../../../services/apiService";
import logger from "../../../utils/logger";


/**
 * API Explorer Component
 * Allows users to explore and test API endpoints
 * @returns {JSX.Element}
 */
const ApiExplorer = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [endpointParams, setEndpointParams] = useState({});
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [baseUrl, setBaseUrl] = useState(Constants.BASE_URL);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [endpointToExecute, setEndpointToExecute] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [showResponsePanel, setShowResponsePanel] = useState(true);
  const [groupByMethod, setGroupByMethod] = useState(false);
  const [methodGroupedEndpoints, setMethodGroupedEndpoints] = useState({});
  const [apiCallResults, setApiCallResults] = useState({});

  // Group endpoints by HTTP method
  useEffect(() => {
    const methods = {};

    // Collect all endpoints across categories
    const allEndpoints = [];
    Object.entries(endpointCategories).forEach(([category, endpoints]) => {
      endpoints.forEach(endpoint => {
        allEndpoints.push({
          ...endpoint,
          category, // Add the original category for reference
        });
      });
    });

    // Group them by method
    allEndpoints.forEach(endpoint => {
      const method = endpoint.method;
      if (!methods[method]) {
        methods[method] = [];
      }
      methods[method].push(endpoint);
    });

    setMethodGroupedEndpoints(methods);
  }, []);

  // Log when component mounts and get current user
  useEffect(() => {
    logger.log("API Explorer component mounted");
    const user = localService.getUser();
    setCurrentUser(user);
    logger.log("Current user:", user);
    logger.log("Base URL:", Constants.BASE_URL);
    setInitialized(true);
  }, []);

  // When the current user changes, update the endpoint params if an endpoint is selected
  useEffect(() => {
    if (selectedEndpoint && currentUser) {
      setEndpointParams(getQueryParams(selectedEndpoint, currentUser));
    }
  }, [currentUser, selectedEndpoint]);

  // Special handling for searchUsers endpoint
  useEffect(() => {
    if (selectedEndpoint && selectedEndpoint.fnName === "searchUsers") {
      setEndpointParams(prev => ({
        ...prev,
        searchTerm: "test", // Default search term
      }));
    }
  }, [selectedEndpoint]);

  /**
   * Handle endpoint selection
   * @param {Object} endpoint - The endpoint to select
   */
  const handleSelectEndpoint = endpoint => {
    setSelectedEndpoint(endpoint);
    setEndpointParams(getQueryParams(endpoint, currentUser));
    setResponse(null);
    setShowResponsePanel(true);
  };

  /**
   * Handle parameter changes
   * @param {string} param - The parameter name
   * @param {any} value - The parameter value
   */
  const handleParamChange = (param, value) => {
    setEndpointParams(prev => ({
      ...prev,
      [param]: value,
    }));
  };

  /**
   * Handle form submission
   */
  const handleSubmit = () => {
    // Always show confirmation for DELETE operations
    if (selectedEndpoint.method === "DELETE") {
      setShowConfirmationModal(true);
      setEndpointToExecute(selectedEndpoint);
    }
    // If we're in production mode, show confirmation dialog for modifying operations
    else if (
      baseUrl.includes(".spacia.africa") &&
      !baseUrl.includes("sandbox") &&
      ["POST", "PUT", "PATCH"].includes(selectedEndpoint.method)
    ) {
      setShowConfirmationModal(true);
      setEndpointToExecute(selectedEndpoint);
    } else {
      executeEndpoint(selectedEndpoint);
    }
  };

  /**
   * Confirm execution of endpoint
   */
  const confirmExecution = () => {
    setShowConfirmationModal(false);
    executeEndpoint(endpointToExecute);
  };

  /**
   * Cancel execution of endpoint
   */
  const cancelExecution = () => {
    setShowConfirmationModal(false);
    setEndpointToExecute(null);
  };

  /**
   * Toggle the response panel
   */
  const toggleResponsePanel = () => {
    setShowResponsePanel(!showResponsePanel);
  };

  /**
   * Execute an API call for an endpoint
   * @param {Object} endpoint - The endpoint to execute
   */
  const executeEndpoint = endpoint => {
    // All options needed by the executeApiCall function
    const options = {
      userId: currentUser?.id || 1,
      baseUrl,
      apiService,
      setApiCallResults,
      setResponse,
      setLoading,
    };

    // Call the simplified executeApiCall function
    executeApiCall(endpoint, endpointParams, options);
  };

  return (
    <StyledContainer fluid>
      <Toaster position="bottom-right" theme="light" closeButton />

      <Header
        currentUser={currentUser}
        baseUrl={baseUrl}
        setBaseUrl={setBaseUrl}
        groupByMethod={groupByMethod}
        setGroupByMethod={setGroupByMethod}
      />

      <Row>
        <Col md={selectedEndpoint && showResponsePanel ? 6 : 12}>
          <EndpointList
            categories={groupByMethod ? methodGroupedEndpoints : endpointCategories}
            onSelectEndpoint={handleSelectEndpoint}
            apiCallResults={apiCallResults}
            groupByMethod={groupByMethod}
          />
        </Col>

        {selectedEndpoint && showResponsePanel && (
          <Col md={6}>
            <ResponsePanel
              selectedEndpoint={selectedEndpoint}
              endpointParams={endpointParams}
              handleParamChange={handleParamChange}
              handleSubmit={handleSubmit}
              loading={loading}
              response={response}
              baseUrl={baseUrl}
              toggleResponsePanel={toggleResponsePanel}
            />
          </Col>
        )}

        {selectedEndpoint && !showResponsePanel && (
          <div style={{ position: "fixed", bottom: "20px", right: "20px" }}>
            <Button variant="primary" onClick={toggleResponsePanel} title="Show response panel">
              Show Panel
            </Button>
          </div>
        )}
      </Row>

      <ConfirmationModal
        show={showConfirmationModal}
        onHide={cancelExecution}
        onConfirm={confirmExecution}
        endpoint={endpointToExecute}
        baseUrl={baseUrl}
      />
    </StyledContainer>
  );
};

export default ApiExplorer;
